/**
 * EF-GLOBAL-COUNTRY-REF-01: Searchable Country Dropdown Component
 * EVENTSFLO-UNIVERSAL-COUNTRY-DROPDOWN-AND-VENUE-ADDRESS-FIELDS-V1:
 *   Added `valueMode` (default "name") so callers whose backend field
 *   is ISO-2 (`Client.billing_country`, max_length=2) can store the
 *   two-letter code while still showing the human-readable country
 *   name in the trigger + option list. Also added common English
 *   aliases ("UK", "USA", "Great Britain") to the search filter so
 *   operators don't need to type the exact ISO name.
 *
 * Features:
 * - Fetches countries from /api/countries (full ISO-3166 list)
 * - Searchable dropdown with type-to-filter
 * - Keyboard navigable (arrow keys + enter)
 * - Shows only is_active = true countries
 * - `valueMode="name"` (default) — stores full country name string
 *   (backwards compatible with existing Venue / VenueLocation usage)
 * - `valueMode="iso2"` — stores the ISO-2 code (uppercase). Empty
 *   string / null clears the value.
 * - Preserves existing values even if not in the ISO list
 * - Logs console warning for non-ISO values (optional)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Check, ChevronsUpDown, Loader2, AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import api from "../../lib/api";

// Cache for countries data to avoid repeated fetches
let countriesCache = null;
let cachePromise = null;

/**
 * Fetch countries from API with caching
 */
async function fetchCountries() {
  if (countriesCache) {
    return countriesCache;
  }
  
  if (cachePromise) {
    return cachePromise;
  }
  
  cachePromise = api.get("/countries")
    .then(response => {
      // Filter to only active countries (API already does this, but belt & braces)
      countriesCache = (response.data || []).filter(c => c.is_active !== false);
      return countriesCache;
    })
    .catch(err => {
      console.error("Failed to fetch countries:", err);
      cachePromise = null; // Allow retry on error
      return [];
    });
  
  return cachePromise;
}

/**
 * Clear the countries cache (useful for testing or when data changes)
 */
export function clearCountriesCache() {
  countriesCache = null;
  cachePromise = null;
}

/**
 * CountrySelect Component
 *
 * @param {Object} props
 * @param {string} props.value - Current stored value. If `valueMode="name"`
 *   this is the full country name (e.g. "United Kingdom"). If
 *   `valueMode="iso2"` this is the two-letter ISO code (e.g. "GB").
 * @param {function} props.onValueChange - Callback when value changes.
 *   Receives the same shape as `value` (name or ISO-2 depending on mode).
 * @param {"name"|"iso2"} [props.valueMode="name"] - Storage mode.
 * @param {string} props.placeholder - Placeholder text (default: "Select country...")
 * @param {boolean} props.disabled - Disable the dropdown
 * @param {string} props.className - Additional CSS classes for trigger button
 * @param {string} props.testId - data-testid attribute
 * @param {boolean} props.required - Show required indicator
 */
export function CountrySelect({
  value,
  onValueChange,
  valueMode = "name",
  placeholder = "Select country...",
  disabled = false,
  className,
  testId = "country-select",
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const hasLoggedWarning = useRef(false);

  // Load countries on mount
  useEffect(() => {
    let mounted = true;

    fetchCountries().then(data => {
      if (mounted) {
        setCountries(data);
        setLoading(false);
      }
    });

    return () => { mounted = false; };
  }, []);

  // Resolve the current stored value to a country row so we can display
  // the human-readable name regardless of valueMode.
  const currentCountry = useMemo(() => {
    if (!value || loading) return null;
    if (valueMode === "iso2") {
      const code = String(value).trim().toUpperCase();
      return countries.find(c => (c.iso2_code || "").toUpperCase() === code) || null;
    }
    return countries.find(c => c.name === value) || null;
  }, [value, countries, loading, valueMode]);

  // Check if current value exists in ISO list and log warning if not
  useEffect(() => {
    if (!loading && value && countries.length > 0 && !hasLoggedWarning.current) {
      if (!currentCountry) {
        console.warn(
          `[CountrySelect] Value "${value}" (mode=${valueMode}) is not in ISO-3166 ` +
          "country list. It will be displayed but may need manual correction."
        );
        hasLoggedWarning.current = true;
      }
    }
  }, [loading, value, countries, currentCountry, valueMode]);

  // Reset warning flag when value changes
  useEffect(() => {
    hasLoggedWarning.current = false;
  }, [value]);

  // Filter countries based on search query. Supports common English
  // aliases so users can type UK / USA / Great Britain / America etc.
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) {
      return countries;
    }
    const query = searchQuery.toLowerCase().trim();
    const ALIASES = {
      uk: "GB",
      "great britain": "GB",
      britain: "GB",
      england: "GB",
      usa: "US",
      america: "US",
      "united states of america": "US",
    };
    const aliasIso = ALIASES[query];
    return countries.filter(country => {
      const nameLc = (country.name || "").toLowerCase();
      const iso2Lc = (country.iso2_code || "").toLowerCase();
      const iso3Lc = (country.iso3_code || "").toLowerCase();
      if (nameLc.includes(query) || iso2Lc.includes(query) || iso3Lc.includes(query)) {
        return true;
      }
      return aliasIso && (country.iso2_code || "").toUpperCase() === aliasIso;
    });
  }, [countries, searchQuery]);

  // Check if current value is a non-ISO value that needs to be shown
  const isNonIsoValue = useMemo(() => {
    if (!value || loading) return false;
    return currentCountry === null;
  }, [value, currentCountry, loading]);

  // Handle selection — emit the correct shape per valueMode
  const handleSelect = useCallback((country) => {
    if (valueMode === "iso2") {
      onValueChange((country.iso2_code || "").toUpperCase());
    } else {
      onValueChange(country.name);
    }
    setOpen(false);
    setSearchQuery("");
  }, [onValueChange, valueMode]);

  // Display value in the trigger button — always the human-readable
  // name so the user isn't reading raw ISO codes.
  const displayValue = currentCountry ? currentCountry.name : (value || placeholder);
  const hasValue = !!value;

  // Comparator: does this ISO row match the currently-stored value?
  const isSelected = useCallback((country) => {
    if (!value) return false;
    if (valueMode === "iso2") {
      return (country.iso2_code || "").toUpperCase() === String(value).trim().toUpperCase();
    }
    return country.name === value;
  }, [value, valueMode]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select country"
          disabled={disabled || loading}
          className={cn(
            "w-full justify-between font-normal",
            !hasValue && "text-muted-foreground",
            isNonIsoValue && "border-amber-500/50",
            className
          )}
          data-testid={testId}
        >
          <span className="truncate flex items-center gap-2">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading countries...
              </>
            ) : (
              <>
                {isNonIsoValue && (
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" title="This value is not in the ISO country list" />
                )}
                {displayValue}
              </>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search country..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? "Loading..." : "No country found."}
            </CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-y-auto">
              {/* Show current non-ISO value at top if it exists */}
              {isNonIsoValue && value && (
                <CommandItem
                  value={String(value)}
                  onSelect={() => {
                    // Non-ISO passthrough — keep whatever the caller
                    // already stored (name string or bare code).
                    onValueChange(value);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="bg-amber-500/10 border-b border-amber-500/20"
                  data-testid={`${testId}-option-current`}
                >
                  <Check className="mr-2 h-4 w-4 opacity-100" />
                  <span className="flex items-center gap-2">
                    <AlertCircle className="h-3 w-3 text-amber-500" />
                    {value}
                    <span className="text-xs text-amber-500">(current value)</span>
                  </span>
                </CommandItem>
              )}
              {/* ISO countries list */}
              {filteredCountries.map((country) => (
                <CommandItem
                  key={country.iso2_code || country.name}
                  value={country.name}
                  onSelect={() => handleSelect(country)}
                  data-testid={`${testId}-option-${country.iso2_code}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      isSelected(country) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex-1">{country.name}</span>
                  {country.iso2_code && (
                    <span className="text-xs text-muted-foreground ml-2">
                      {country.iso2_code}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CountrySelect;
